import { PageSection, Tab, Tabs, TabTitleText } from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { VisitListComponent } from '@app/components/visit/list'
import { useListVisits } from '@app/domain/visit/visit.hooks'
import {
  OpenVisitStatusIds,
  ClosedVisitStatusIds,
} from '@app/domain/visit/visit.interface'
import { getLocalDate } from '@app/lib/time'
import type { AppState } from '@app/state'
import * as TodayState from '@app/state/today'

const getDateString = () => getLocalDate(new Date())

export const TodaysVisitList = () => {
  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState<TodayState.TodayTab>(
    TodayState.TodayTab.Incomplete,
  )
  const [date] = useState<string>(getDateString())

  const {
    locationFilter,
    mrnFilter,
    patientNameFilter,
    statusFilters,
    typeFilter,
    pageNumber,
    recordsPerPage,
    sortDirection,
    sortField,
  } = useSelector<AppState, TodayState.State>((state) => state.today)

  const { visits, totalNumberOfRecords, getVisits } = useListVisits()

  const statusFilter = React.useMemo(() => {
    return statusFilters[activeTab]
  }, [activeTab, statusFilters])

  const allowedStatuses = React.useMemo(() => {
    return activeTab === TodayState.TodayTab.Incomplete
      ? OpenVisitStatusIds
      : ClosedVisitStatusIds
  }, [activeTab])

  const refreshList = useCallback(() => {
    const statuses = statusFilter.length > 0 ? statusFilter : allowedStatuses
    getVisits(
      pageNumber,
      recordsPerPage,
      locationFilter,
      mrnFilter,
      patientNameFilter,
      statuses,
      date,
      null,
      sortDirection,
      sortField,
      typeFilter,
    )
  }, [
    statusFilter,
    allowedStatuses,
    getVisits,
    pageNumber,
    recordsPerPage,
    locationFilter,
    mrnFilter,
    patientNameFilter,
    date,
    sortDirection,
    sortField,
    typeFilter,
  ])

  const handleTabSelect = useCallback((_, selectedTab) => {
    setActiveTab(selectedTab)
  }, [])

  useEffect(() => {
    refreshList()
  }, [refreshList])

  const onSetPage = useCallback(
    (pageNumber: number) => {
      dispatch(TodayState.actions.setPageNumber(pageNumber))
    },
    [dispatch],
  )

  const onPerPageSelect = useCallback(
    (recordsPerPage: number) => {
      dispatch(TodayState.actions.setPageNumber(1))
      dispatch(TodayState.actions.setRecordsPerPage(recordsPerPage))
    },
    [dispatch],
  )

  const setLocation = useCallback(
    (newLocation) => {
      dispatch(TodayState.actions.setPageNumber(1))
      dispatch(TodayState.actions.setLocationFilter(newLocation))
    },
    [dispatch],
  )

  const setMrn = useCallback(
    (newMrn) => {
      dispatch(TodayState.actions.setPageNumber(1))
      dispatch(TodayState.actions.setMrnFilter(newMrn))
    },
    [dispatch],
  )

  const setPatientName = useCallback(
    (newPatientName) => {
      dispatch(TodayState.actions.setPageNumber(1))
      dispatch(TodayState.actions.setPatientNameFilter(newPatientName))
    },
    [dispatch],
  )

  const onSetSort = useCallback(
    (newField: string, newDirection: string) => {
      dispatch(TodayState.actions.setPageNumber(1))
      dispatch(TodayState.actions.setSort(newField, newDirection))
    },
    [dispatch],
  )

  const setStatus = useCallback(
    (newStatus) => {
      dispatch(TodayState.actions.setPageNumber(1))
      dispatch(TodayState.actions.setStatus(activeTab, newStatus))
    },
    [activeTab, dispatch],
  )

  const setType = useCallback(
    (newTypes) => {
      dispatch(TodayState.actions.setPageNumber(1))
      dispatch(TodayState.actions.setType(newTypes))
    },
    [dispatch],
  )

  return (
    <PageSection className="pf-m-light">
      <Tabs activeKey={activeTab} onSelect={handleTabSelect}>
        <Tab
          eventKey={TodayState.TodayTab.Incomplete}
          title={<TabTitleText>Upcoming & In Progress</TabTitleText>}
        />
        <Tab
          eventKey={TodayState.TodayTab.Complete}
          title={<TabTitleText>Completed</TabTitleText>}
        />
      </Tabs>
      <VisitListComponent
        filters={{
          location: locationFilter,
          onLocationUpdated: setLocation,
          mrn: mrnFilter,
          onMrnUpdated: setMrn,
          patientName: patientNameFilter,
          onPatientNameUpdated: setPatientName,
          status: statusFilter,
          onStatusUpdated: setStatus,
          type: typeFilter,
          onTypeUpdated: setType,
        }}
        pagination={{
          onSetPage,
          onPerPageSelect,
          pageNumber,
          recordsPerPage,
          totalNumberOfRecords,
        }}
        options={{ allowedStatuses: allowedStatuses }}
        onRefresh={refreshList}
        sorting={{
          onSetSort,
          sortField,
          sortDirection,
        }}
        visits={visits}
      />
    </PageSection>
  )
}
